/** Service configurator — product types, modules, and step labels. */

import type { SelectablePageId } from "../lib/configuratorPageCatalog";
import {
  EMBED_CAPABLE_MODULES,
  PAGE_OWNED_MODULE,
} from "../lib/configuratorPageContent";
import {
  isLockedModule,
} from "../lib/configuratorCoreModules";

export type ConfiguratorProductId =
  | "website"
  | "micro-page"
  | "eshop"
  | "portfolio"
  | "web-app"
  | "mobile-app"
  | "game"
  | "branding";

export type ConfiguratorModuleId =
  | "blog"
  | "contact-form"
  | "map-location"
  | "admin-panel"
  | "multilingual"
  | "newsletter"
  | "booking"
  | "testimonials"
  | "faq"
  | "analytics"
  | "product-catalog"
  | "cart-checkout"
  | "payment-gateway"
  | "inventory"
  | "customer-accounts"
  | "project-gallery"
  | "about-page"
  | "user-auth"
  | "dashboard"
  | "api-integration"
  | "notifications"
  | "pricing"
  | "video-embed"
  | "cta-optimization"
  | "offline-mode"
  | "search"
  | "filters"
  | "categories"
  | "figma-handoff"
  | "brand-asset-library"
  | "print-ready-files";

export type ConfiguratorModuleKind = "page-section" | "commerce" | "accounts" | "backend" | "branding";

export type ConfiguratorModule = {
  id: ConfiguratorModuleId;
  title: string;
  description: string;
  previewLabel: string;
  kind: ConfiguratorModuleKind;
};

const MODULE_KIND: Record<ConfiguratorModuleId, ConfiguratorModuleKind> = {
  blog: "page-section",
  "contact-form": "page-section",
  "map-location": "page-section",
  multilingual: "page-section",
  newsletter: "page-section",
  booking: "page-section",
  testimonials: "page-section",
  faq: "page-section",
  "project-gallery": "page-section",
  "about-page": "page-section",
  pricing: "page-section",
  "video-embed": "page-section",
  "cta-optimization": "page-section",
  search: "page-section",
  filters: "commerce",
  categories: "commerce",
  "product-catalog": "commerce",
  "cart-checkout": "commerce",
  "payment-gateway": "commerce",
  "customer-accounts": "accounts",
  "user-auth": "accounts",
  dashboard: "accounts",
  analytics: "backend",
  "admin-panel": "backend",
  "api-integration": "backend",
  notifications: "backend",
  inventory: "backend",
  "offline-mode": "backend",
  "figma-handoff": "branding",
  "brand-asset-library": "branding",
  "print-ready-files": "branding",
};

function moduleEntry(
  id: ConfiguratorModuleId,
  title: string,
  description: string,
  previewLabel: string,
): ConfiguratorModule {
  return { id, title, description, previewLabel, kind: MODULE_KIND[id] };
}

export type ConfiguratorProduct = {
  id: ConfiguratorProductId;
  title: string;
  description: string;
  baseWeeks: [number, number];
  basePrice: string;
  modules: ConfiguratorModuleId[];
};

const MODULES: Record<ConfiguratorModuleId, ConfiguratorModule> = {
  blog: moduleEntry(
    "blog",
    "Blog feed on other pages",
    "Recent posts block on home, shop, or about — the full Blog page comes from step 2.",
    "Blog feed",
  ),
  "contact-form": moduleEntry(
    "contact-form",
    "Contact form on other pages",
    "Enquiry form on home or pricing — the Contact page includes its own form from step 2.",
    "Contact",
  ),
  "map-location": moduleEntry(
    "map-location",
    "Map & location",
    "Office location, directions, and local presence.",
    "Location",
  ),
  "admin-panel": moduleEntry(
    "admin-panel",
    "Admin CMS",
    "Back-office to manage content, products, or orders.",
    "Admin",
  ),
  multilingual: moduleEntry(
    "multilingual",
    "Multilingual",
    "Multiple languages with a language switcher.",
    "Languages",
  ),
  newsletter: moduleEntry(
    "newsletter",
    "Newsletter",
    "Email signup and list integration.",
    "Newsletter",
  ),
  booking: moduleEntry(
    "booking",
    "Booking",
    "Appointments, reservations, or scheduling.",
    "Booking",
  ),
  testimonials: moduleEntry(
    "testimonials",
    "Testimonials",
    "Client quotes and social proof sections.",
    "Reviews",
  ),
  faq: moduleEntry(
    "faq",
    "FAQ on other pages",
    "Accordion sections on product detail, home, or pricing — the FAQ page itself is step 2.",
    "FAQ embed",
  ),
  analytics: moduleEntry(
    "analytics",
    "Analytics",
    "Traffic tracking and conversion measurement.",
    "Analytics",
  ),
  "product-catalog": moduleEntry(
    "product-catalog",
    "Product catalog",
    "Categories, filters, and product detail pages.",
    "Products",
  ),
  "cart-checkout": moduleEntry(
    "cart-checkout",
    "Cart & checkout",
    "Shopping cart and streamlined purchase flow.",
    "Checkout",
  ),
  "payment-gateway": moduleEntry(
    "payment-gateway",
    "Payments",
    "Card payments and order confirmation.",
    "Payments",
  ),
  inventory: moduleEntry(
    "inventory",
    "Inventory",
    "Stock levels and product availability.",
    "Stock",
  ),
  "customer-accounts": moduleEntry(
    "customer-accounts",
    "Customer accounts",
    "Order history, saved details, and profile.",
    "Accounts",
  ),
  "project-gallery": moduleEntry(
    "project-gallery",
    "Project gallery",
    "Visual showcase of your work.",
    "Gallery",
  ),
  "about-page": moduleEntry(
    "about-page",
    "About page",
    "Story, team, and credibility content.",
    "About",
  ),
  "user-auth": moduleEntry(
    "user-auth",
    "Login & registration",
    "Sign up, login, and role-based access.",
    "Sign in",
  ),
  dashboard: moduleEntry(
    "dashboard",
    "Dashboard",
    "Main app view with key metrics and actions.",
    "Dashboard",
  ),
  "api-integration": moduleEntry(
    "api-integration",
    "API integrations",
    "Connect to external services and data sources.",
    "Integrations",
  ),
  notifications: moduleEntry(
    "notifications",
    "Notifications",
    "Email or in-app alerts for key events.",
    "Alerts",
  ),
  pricing: moduleEntry(
    "pricing",
    "Pricing page",
    "Plans, tiers, and comparison for SaaS or services.",
    "Pricing",
  ),
  "video-embed": moduleEntry(
    "video-embed",
    "Video embed",
    "YouTube or Vimeo demo, founder story, or product walkthrough.",
    "Video",
  ),
  "cta-optimization": moduleEntry(
    "cta-optimization",
    "CTA optimization",
    "Sticky calls-to-action, A/B variants, and conversion-focused layout.",
    "CTA",
  ),
  search: moduleEntry(
    "search",
    "Site search",
    "Search bar for pages, products, and articles.",
    "Search",
  ),
  filters: moduleEntry(
    "filters",
    "Filters",
    "Filter products by price, size, colour, and attributes.",
    "Filters",
  ),
  categories: moduleEntry(
    "categories",
    "Shop categories",
    "Product categories, collections, and navigation.",
    "Categories",
  ),
  "offline-mode": moduleEntry(
    "offline-mode",
    "Offline mode",
    "Cached content and core flows when the network drops.",
    "Offline",
  ),
  "figma-handoff": moduleEntry(
    "figma-handoff",
    "Figma handoff",
    "Component library, styles, and dev-ready specs in Figma.",
    "Figma library",
  ),
  "brand-asset-library": moduleEntry(
    "brand-asset-library",
    "Brand asset library",
    "Organised folder of logos, colours, type, and templates for your team.",
    "Asset library",
  ),
  "print-ready-files": moduleEntry(
    "print-ready-files",
    "Print-ready exports",
    "CMYK PDFs and bleed-ready files for business cards, stationery, and signage.",
    "Print files",
  ),
};

export const CONFIGURATOR_PRODUCTS: ConfiguratorProduct[] = [
  {
    id: "website",
    title: "Website",
    description: "Marketing site or company presence that converts visitors.",
    baseWeeks: [3, 6],
    basePrice: "From €550",
    modules: [
      "map-location",
      "testimonials",
      "newsletter",
      "multilingual",
      "search",
      "faq",
      "blog",
      "booking",
      "admin-panel",
      "analytics",
    ],
  },
  {
    id: "micro-page",
    title: "Micro page / Landing",
    description: "One focused page — campaign, launch, waitlist, or simple presence.",
    baseWeeks: [1, 2],
    basePrice: "From €260",
    modules: [
      "newsletter",
      "testimonials",
      "video-embed",
      "payment-gateway",
      "cta-optimization",
      "booking",
      "faq",
      "multilingual",
      "analytics",
    ],
  },
  {
    id: "eshop",
    title: "E-shop",
    description: "Online store with products, checkout, and trust signals.",
    baseWeeks: [6, 10],
    basePrice: "From €2,500",
    modules: [
      "customer-accounts",
      "inventory",
      "filters",
      "categories",
      "search",
      "map-location",
      "newsletter",
      "analytics",
    ],
  },
  {
    id: "portfolio",
    title: "Portfolio",
    description: "Showcase work, story, and ways to get in touch.",
    baseWeeks: [2, 4],
    basePrice: "From €450",
    modules: ["testimonials", "newsletter", "map-location", "analytics"],
  },
  {
    id: "web-app",
    title: "Web app",
    description: "SaaS, dashboards, or tools people log into and use daily.",
    baseWeeks: [8, 16],
    basePrice: "From €8,000",
    modules: [
      "user-auth",
      "admin-panel",
      "api-integration",
      "notifications",
      "search",
      "offline-mode",
      "analytics",
      "multilingual",
    ],
  },
  {
    id: "game",
    title: "Game & Interactive",
    description: "In-game UI, playful interfaces, and interactive experiences.",
    baseWeeks: [8, 16],
    basePrice: "On request",
    modules: ["user-auth", "notifications", "api-integration", "analytics", "multilingual"],
  },
  {
    id: "mobile-app",
    title: "Mobile app",
    description: "iOS and Android experience with native-feeling flows.",
    baseWeeks: [10, 20],
    basePrice: "From €12,000",
    modules: [
      "user-auth",
      "notifications",
      "payment-gateway",
      "api-integration",
      "search",
      "offline-mode",
      "analytics",
      "multilingual",
      "contact-form",
    ],
  },
  {
    id: "branding",
    title: "Branding",
    description: "Identity, visual system, and brand guidelines.",
    baseWeeks: [2, 4],
    basePrice: "From €800",
    modules: ["figma-handoff", "brand-asset-library", "print-ready-files"],
  },
];

export const CONFIGURATOR_STEPS = [
  { id: 1, label: "Project" },
  { id: 2, label: "Pages" },
  { id: 3, label: "Features" },
  { id: 4, label: "Timeline" },
  { id: 5, label: "Send brief" },
] as const;

export const CONFIGURATOR_INDUSTRIES = [
  "Technology",
  "Healthcare",
  "Finance",
  "Hospitality",
  "E-commerce",
  "Education",
  "Creative / agency",
  "Food & beverage",
  "Real estate",
  "Other",
] as const;

export function getConfiguratorProduct(id: ConfiguratorProductId): ConfiguratorProduct | undefined {
  return CONFIGURATOR_PRODUCTS.find((p) => p.id === id);
}

/** Commerce, accounts, and backend unlocked by page selection. */
const PAGE_ACTIVATED_MODULES: Partial<Record<SelectablePageId, ConfiguratorModuleId[]>> = {
  shop: ["customer-accounts", "inventory", "filters", "categories"],
  login: ["user-auth"],
  dashboard: ["dashboard"],
};

const PAGE_ONLY_SECTIONS = new Set<ConfiguratorModuleId>([
  "about-page",
  "project-gallery",
  "pricing",
  "booking",
]);

export const FEATURE_GROUP_LABELS: Record<ConfiguratorModuleKind, string> = {
  "page-section": "Page sections",
  commerce: "Store & checkout",
  accounts: "Accounts & access",
  backend: "Backend & integrations",
  branding: "Handoff & delivery",
};

export function getModulesForProduct(productId: ConfiguratorProductId): ConfiguratorModule[] {
  const product = getConfiguratorProduct(productId);
  if (!product) return [];
  return product.modules.map((id) => MODULES[id]).filter(Boolean);
}

export function getAvailableModulesForProduct(
  productId: ConfiguratorProductId,
  selectedPages: SelectablePageId[] = [],
): ConfiguratorModule[] {
  const product = getConfiguratorProduct(productId);
  if (!product) return [];
  const ids = new Set<ConfiguratorModuleId>(product.modules);

  for (const pageId of selectedPages) {
    for (const modId of PAGE_ACTIVATED_MODULES[pageId] ?? []) {
      ids.add(modId);
    }
    const owned = PAGE_OWNED_MODULE[pageId];
    if (owned && EMBED_CAPABLE_MODULES.includes(owned)) {
      ids.add(owned);
    }
  }

  for (const id of ids) {
    if (isPageOnlyModule(id, selectedPages)) {
      ids.delete(id);
    }
    if (isLockedModule(productId, selectedPages, id)) {
      ids.delete(id);
    }
  }

  return [...ids].map((id) => MODULES[id]).filter(Boolean);
}

export { isLockedModule, getCoreModulesForState, mergeWithCoreModules, syncModulesWithScope } from "../lib/configuratorCoreModules";

function isPageOnlyModule(moduleId: ConfiguratorModuleId, selectedPages: SelectablePageId[]): boolean {
  if (EMBED_CAPABLE_MODULES.includes(moduleId) || !PAGE_ONLY_SECTIONS.has(moduleId)) {
    return false;
  }
  const pageIds = Object.entries(PAGE_OWNED_MODULE)
    .filter(([, mod]) => mod === moduleId)
    .map(([pageId]) => pageId as SelectablePageId);
  return pageIds.some((p) => selectedPages.includes(p));
}

export type FeatureModuleGroup = {
  kind: ConfiguratorModuleKind;
  label: string;
  modules: ConfiguratorModule[];
};

export function getFeatureGroupsForProduct(
  productId: ConfiguratorProductId,
  selectedPages: SelectablePageId[] = [],
): FeatureModuleGroup[] {
  const modules = getAvailableModulesForProduct(productId, selectedPages);
  const kinds: ConfiguratorModuleKind[] =
    productId === "branding"
      ? ["branding"]
      : ["page-section", "commerce", "accounts", "backend"];
  return kinds
    .map((kind) => ({
      kind,
      label: FEATURE_GROUP_LABELS[kind],
      modules: modules.filter((m) => m.kind === kind),
    }))
    .filter((g) => g.modules.length > 0);
}

/** Feature modules (capabilities within pages — not navigation pages). */
export const getFeaturesForProduct = getAvailableModulesForProduct;

export function getModule(id: ConfiguratorModuleId): ConfiguratorModule {
  return MODULES[id];
}

/** Map wizard service ids (capability flows) to configurator product. */
export const WIZARD_SERVICE_TO_CONFIGURATOR: Partial<
  Record<string, ConfiguratorProductId>
> = {
  website: "website",
  eshop: "eshop",
  portfolio: "portfolio",
  "web-app": "web-app",
  "ios-app": "mobile-app",
  "android-app": "mobile-app",
  "custom-software": "web-app",
  "games-interactive": "game",
  "branding-identity": "branding",
  "full-product": "web-app",
  "micro-page": "micro-page",
};

/** Map /start-project ?product= ids to configurator product. */
export const PRODUCT_PARAM_TO_CONFIGURATOR: Record<string, ConfiguratorProductId> = {
  website: "website",
  "micro-page": "micro-page",
  eshop: "eshop",
  portfolio: "portfolio",
  "web-app": "web-app",
  "mobile-app": "mobile-app",
  game: "game",
  branding: "branding",
  "brand-identity": "branding",
  "games-interactive": "game",
};

